<template>
  <div class="group-rounded-box my-0 mx-4">
    <span class="title">Offers Breakdown</span>

    <!-- ROW -->
    <div class="row">
      <!-- MAIN COLUMN -->
      <div class="col-12 pb-3 px-4">
        <!-- TABLE CONTAINER -->
        <div class="table-container">
          <!-- SELLER HAS NOT UPDATED DAILY SETTLEMENTS -->
          <div v-if="getPrices.length < 1" class="w-100 alert tables-alert-custom-message-notify mt-3 text-center">
            <i class="fas fa-info-circle mr-2"></i>
            Offers will not be displayed until settlement prices have been updated
          </div>
          <!-- SELLER HAS NOT SELECTED THE REQUIRED FILTERS -->
          <div v-else-if="!allFiltersSelected" class="w-100 alert tables-alert-custom-message-notify mt-3 text-center">
            <i class="fas fa-info-circle mr-2"></i>
            Please select a Customer and a Terminal/Network to see available offers
          </div>
          <!-- SERVER ERROR -->
          <div v-else-if="isServerError" class="alert alert-t_red mt-3 text-center">
            <i class="fas fa-info-circle mr-2"></i>
            Server Error: Unable to fetch offers, please refresh
          </div>
          <!-- NO OFFERS FOUND -->
          <div
            v-else-if="availableOffersList.length < 1"
            class="alert tables-alert-custom-message-notify mt-3 text-center"
          >
            <i class="fas fa-info-circle mr-2"></i>
            <span v-if="isBasketCreated">No offers available for this selection</span>
            <span v-else>You have not created any offers for this market</span>
          </div>

          <!-- OFFERS FOUND -->
          <table v-else class="table responsive-table table-borderless">
            <!-- TABLE LAYOUT FOR LARGE SCREENS -->
            <tbody v-if="!$breakpoint.mdAndDown" class="tbody-py-3">
              <tr v-if="offerBlocks[offerBlockSelected]">
                <td :colspan="OFFER_BLOCK_MAX_LENGTH + 1" class="text-center pb-3">
                  <button
                    @click="selectOfferBlock('PREV')"
                    :disabled="offerBlockSelected === 0"
                    class="btn btn-link mx-4"
                  >
                    <i class="fas fa-lg fa-chevron-left mr-2"></i>Previous
                    {{ offerBlockSelected > 0 ? `${offerBlocks[offerBlockSelected - 1].length} offers` : `` }}
                  </button>
                  <button
                    @click="selectOfferBlock('NEXT')"
                    :disabled="offerBlockSelected === offerBlocks.length - 1"
                    class="btn btn-link mx-4"
                  >
                    Next
                    {{
                      offerBlocks.length - 1 > offerBlockSelected
                        ? `${offerBlocks[offerBlockSelected + 1].length} offers`
                        : ``
                    }}<i class="fas fa-lg fa-chevron-right ml-2"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="!isNetworkSelected">
                <th>Product</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key" class="font-weight-bold heading-medium w-25">{{ offer.fuel_id | fueltype }}</td>
                </template>
                <!-- EMPTY TABLE DATA CELLS THAT SPAN TO COMPLETE THE MAXIMUM LENGTH OF THE TABLE  -->
                <td v-for="n in offerBlockLengthDiffFromMaxLength" :key="`spanner-td-${n}`" class="w-25"></td>
              </tr>
              <tr v-else>
                <th>Network</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key" class="font-weight-bold heading-medium w-25">
                    {{ getNetworkName(offer.networkId, networks) }}
                  </td>
                </template>
                <!-- EMPTY TABLE DATA CELLS THAT SPAN TO COMPLETE THE MAXIMUM LENGTH OF THE TABLE  -->
                <td v-for="n in offerBlockLengthDiffFromMaxLength" :key="`spanner-td-${n}`" class="w-25"></td>
              </tr>
              <tr v-if="isContract">
                <th>Contract Ref.</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">{{ offer.contract.reference }}</td>
                </template>
              </tr>
              <tr>
                <th>Measurement</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">{{ offer.fuel_measurement_id | fuelmeasurementtype }}</td>
                </template>
              </tr>
              <template v-if="auditTrailShown.offer">
                <tr>
                  <th>Pricing Method</th>
                  <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                    <td :key="key">{{ offer.type_id | offerpricingmethod }}</td>
                  </template>
                </tr>
                <tr>
                  <th>Bio Pricing</th>
                  <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                    <td :key="key">{{ offer.fuel_id | offerbiopricing }}</td>
                  </template>
                </tr>
                <tr class="spacing-under">
                  <th>Lifting Period</th>
                  <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                    <td :key="key">
                      <span v-if="!isNetworkSelected"> {{ offer | liftingperiodformat }} </span>
                      <span v-else> &mdash; </span>
                    </td>
                  </template>
                </tr>
                <tr>
                  <th>Product Settlement (USD/mt)</th>
                  <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                    <td :key="key">
                      <span v-if="offer.offerPricingBreakdown && offer.offerPricingBreakdown.baseProduct">{{
                        offer.offerPricingBreakdown.baseProduct.settlement | priceformat
                      }}</span>
                      <span v-else>&mdash;</span>
                    </td>
                  </template>
                </tr>
                <tr>
                  <th>Bio-element Product Settlement (USD/mt)</th>
                  <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                    <td :key="key">
                      <span v-if="offer.offerPricingBreakdown && offer.offerPricingBreakdown.bioElement">{{
                        offer.offerPricingBreakdown.bioElement.settlement | priceformat
                      }}</span>
                      <span v-else>&mdash;</span>
                    </td>
                  </template>
                </tr>
                <tr>
                  <th>FX Settlement GBP/USD</th>
                  <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                    <td :key="key">{{ fxSettlement | fxformat }}</td>
                  </template>
                </tr>
                <tr>
                  <th>Density Conversion (L/mt)</th>
                  <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                    <td :key="key">
                      <span
                        v-if="
                          isPricingEngineEnabled &&
                          offer.offerPricingBreakdown &&
                          offer.offerPricingBreakdown.baseProduct
                        "
                        >{{ offer.offerPricingBreakdown.baseProduct.density | priceformat }}</span
                      >
                      <span v-else-if="offer.fuel && offer.fuel.lpt != null && offer.fuel.type === 'SPOT'">{{
                        offer.fuel.lpt | priceformat
                      }}</span>
                      <span v-else>&mdash;</span>
                    </td>
                  </template>
                </tr>
                <tr>
                  <th>Bio-element Density Conversion (L/mt)</th>
                  <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                    <td :key="key">
                      <span
                        v-if="
                          isPricingEngineEnabled &&
                          offer.offerPricingBreakdown &&
                          offer.offerPricingBreakdown.bioElement
                        "
                        >{{ offer.offerPricingBreakdown.bioElement.density | priceformat }}</span
                      >
                      <span v-else-if="offer.fuel && offer.fuel.lpt != null && offer.fuel.type === 'NOT4SALE'">{{
                        offer.fuel.lpt | priceformat
                      }}</span>
                      <span v-else>&mdash;</span>
                    </td>
                  </template>
                </tr>
                <tr>
                  <th>Duty (ppl)</th>
                  <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                    <td :key="key">
                      <span
                        v-if="
                          isPricingEngineEnabled &&
                          offer.offerPricingBreakdown &&
                          offer.offerPricingBreakdown.baseProduct
                        "
                        >{{ offer.offerPricingBreakdown.baseProduct.duty | priceformat }}</span
                      >
                      <span v-else-if="offer.fuel && offer.fuel.duty != null && offer.fuel.type === 'SPOT'">{{
                        offer.fuel.duty | priceformat
                      }}</span>
                      <span v-else>&mdash;</span>
                    </td>
                  </template>
                </tr>
                <tr class="spacing-under">
                  <th>Bio-element Duty (ppl)</th>
                  <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                    <td :key="key">
                      <span
                        v-if="
                          isPricingEngineEnabled &&
                          offer.offerPricingBreakdown &&
                          offer.offerPricingBreakdown.bioElement
                        "
                        >{{ offer.offerPricingBreakdown.bioElement.duty | priceformat }}</span
                      >
                      <span v-else-if="offer.fuel && offer.fuel.duty != null && offer.fuel.type === 'NOT4SALE'">{{
                        offer.fuel.duty | priceformat
                      }}</span>
                      <span v-else>&mdash;</span>
                    </td>
                  </template>
                </tr>
              </template>
              <tr class="spacing-under">
                <th>
                  <button class="btn btn-link font-style-italic" @click="toggleAuditTrailBreakdown('offer')">
                    <span v-if="!auditTrailShown.offer">Show offer breakdown</span>
                    <span v-else>Hide offer breakdown</span>
                  </button>
                </th>
              </tr>
              <tr>
                <th>Settle Base Price (ppl)</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">
                    <span v-if="offer.offerPricingBreakdown && offer.type_id !== 'SETTLEMENT'">{{
                      offer.offerPricingBreakdown.settleBasePrice | priceformat
                    }}</span>
                    <span v-else>&mdash;</span>
                  </td>
                </template>
              </tr>
              <tr>
                <th>Market Change (ppl)</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">
                    <div v-if="offer.type_id === 'TRACKING' && offer.offerPricingBreakdown">
                      <span v-if="offer.offerPricingBreakdown.productMarketChange >= 0">{{
                        offer.offerPricingBreakdown.productMarketChange | priceformat
                      }}</span>
                      <span v-else class="negative-market-change">
                        {{ offer.offerPricingBreakdown.productMarketChange | priceformat }}
                      </span>
                    </div>
                    <span v-else>&mdash;</span>
                  </td>
                </template>
              </tr>
              <tr class="spacing-under">
                <th>Live Base Price (ppl)</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">
                    <span v-if="offer.type_id === 'TRACKING' && offer.offerPricingBreakdown">{{
                      offer.offerPricingBreakdown.liveBasePrice | priceformat
                    }}</span>
                    <span v-else>&mdash;</span>
                  </td>
                </template>
              </tr>
              <template v-if="isPricingEngineEnabled">
                <!-- COST ADDITIONS BREAKDOWN -->
                <template v-if="auditTrailShown.costAdditions">
                  <tr v-for="(costAdditionHeading, key) of Object.keys(costAdditionHeadings)" :key="`A${key}`">
                    <td>{{ costAdditionHeadings[costAdditionHeading] }}</td>
                    <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                      <td :key="key" class="align-top">
                        <span v-if="ruleInOffer(offer, 'costAdditions', costAdditionHeading)">
                          {{
                            Number(getRuleFromOffer(offer, "costAdditions", costAdditionHeading).action.valueInPpl)
                              | priceformat
                          }}
                        </span>
                        <span v-else> &mdash; </span>
                      </td>
                    </template>
                  </tr>
                  <!-- COST ADDITIONS BREAKDOWN -->
                </template>
                <tr class="spacing-under">
                  <th>
                    Cost Additions (ppl)
                    <button class="btn btn-link font-style-italic" @click="toggleAuditTrailBreakdown('costAdditions')">
                      <span v-if="!auditTrailShown.costAdditions">See full breakdown</span>
                      <span v-else>Hide breakdown</span>
                    </button>
                  </th>
                  <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                    <td :key="key">
                      <span v-if="offer.type_id !== 'FIXED' && offer.offerPricingBreakdown">{{
                        offer.offerPricingBreakdown.costAdditions | priceformat
                      }}</span>
                      <span v-else>&mdash;</span>
                    </td>
                  </template>
                </tr>
              </template>
              <tr v-else>
                <th>Premium (ppl)</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">
                    <span v-if="offer.type_id !== 'FIXED'">{{ offer.premium | priceformat }}</span>
                    <span v-else>&mdash;</span>
                  </td>
                </template>
              </tr>
              <tr v-if="!isContract" :key="`adjustment-${marginRenderKey}`">
                <th>Adjustment (ppl)</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">
                    <cleave
                      class="form-control d-block"
                      :options="numeral"
                      placeholder="Enter adjustment"
                      v-model="offer.adjustment"
                      @keyup.native="updateOfferTotalPrice(offer)"
                      :disabled="!offer.offerPricingBreakdown"
                    ></cleave>
                    <span v-if="offer.type_id !== 'SETTLEMENT' && isInvalidOfferQuotePrice(offer)" class="error-message"
                      >Invalid adjustment price: quote price must be greater than 0</span
                    >
                  </td>
                </template>
              </tr>
              <!-- CUSTOMER MARGIN BREAKDOWN -->
              <template v-if="isPricingEngineEnabled && !isContract && auditTrailShown.customerMargin">
                <tr v-for="(customerMarginHeading, key) of Object.keys(customerMarginHeadings)" :key="`B${key}`">
                  <td>{{ customerMarginHeadings[customerMarginHeading] }}</td>
                  <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                    <td :key="key" class="align-top">
                      <span v-if="ruleInOffer(offer, 'customerMargin', customerMarginHeading)">
                        {{
                          Number(
                            getRuleFromOffer(offer, "customerMargin", customerMarginHeading).action.valueInPpl
                          ).toFixed(2)
                        }}
                      </span>
                      <span v-else> &mdash; </span>
                    </td>
                  </template>
                </tr>
                <!-- CUSTOMER MARGIN BREAKDOWN -->
              </template>
              <tr v-if="!isContract" class="spacing-under">
                <th>
                  Customer Margin (ppl)
                  <button
                    v-if="isPricingEngineEnabled"
                    class="btn btn-link font-style-italic"
                    @click="toggleAuditTrailBreakdown('customerMargin')"
                  >
                    <span v-if="!auditTrailShown.customerMargin">See full breakdown</span>
                    <span v-else>Hide breakdown</span>
                  </button>
                </th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">
                    <span v-if="offer.finalCustomerMargin">{{ offer.finalCustomerMargin | priceformat }}</span>
                    <span v-else>&mdash;</span>
                  </td>
                </template>
              </tr>
              <tr class="spacing-under">
                <th>Haulage (ppl)</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">
                    <span v-if="haulageRate > 0">{{ haulageRate | priceformat }}</span>
                    <span v-else>&mdash;</span>
                  </td>
                </template>
              </tr>
              <tr :key="`quotePrice-${quotePriceRenderKey}`" class="spacing-under">
                <th v-if="isContract">Contract Price (ppl)</th>
                <th v-else>Quote Price (ppl)</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td v-if="offer.type_id === 'SETTLEMENT'" :key="`settlement-${key}`">
                    <span>Pending Settlement</span>
                  </td>
                  <td v-else-if="!isContract" :key="key">
                    <cleave
                      class="form-control d-block"
                      :options="numeral"
                      :value="offer.quotePrice + haulageRate"
                      @keyup.native="onSetOfferQuotePrice($event.target.value, offer)"
                      :disabled="!offer.offerPricingBreakdown"
                    ></cleave>
                    <span v-if="isInvalidOfferQuotePrice(offer)" class="error-message"
                      >Quote price must be greater than 0</span
                    >
                  </td>
                  <td v-else :key="key">{{ offer.quotePrice | priceformat }}</td>
                </template>
              </tr>
              <tr v-if="isContract">
                <th>Current Month Volume/L</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">
                    <span> {{ offer.totalQuantityForMonth | volumeformat }}</span>
                  </td>
                </template>
              </tr>
              <tr v-if="isContract" class="spacing-under">
                <th>Volume Remaining/L</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">
                    <span> {{ offer.currentMaximumAvailable | volumeformat }}</span>
                  </td>
                </template>
              </tr>
              <tr>
                <th>Volume/L</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">
                    <cleave
                      class="form-control d-block"
                      :options="numeralPositive"
                      :placeholder="maxMinPlaceholderValues(offer)"
                      v-model="offer.quantity"
                      @input="updateOfferTotalPrice(offer)"
                      :disabled="!offer.offerPricingBreakdown"
                    ></cleave>
                    <span
                      v-if="checkOfferQuantityStatus(offer) === offerQuantityStatus.invalid"
                      class="error-message"
                      >{{ invalidQuantityErrorMessage(offer) }}</span
                    >
                  </td>
                </template>
              </tr>
              <tr class="spacing-under">
                <th>Total GBP</th>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">{{ (offer.totalPrice + (haulageRate / 100) * offer.quantity) | priceformat }}</td>
                </template>
              </tr>
              <tr>
                <td></td>
                <template v-for="(offer, key) in offerBlocks[offerBlockSelected]">
                  <td :key="key">
                    <div class="col text-center">
                      <button
                        class="btn btn-confirm"
                        @click="$emit('add-offer-to-basket', offer, $event)"
                        :disabled="!areOfferParametersValid(offer)"
                      >
                        Add to Basket
                      </button>
                    </div>
                  </td>
                </template>
              </tr>
              <!-- TABLE LAYOUT FOR LARGE SCREENS -->
            </tbody>

            <!-- TABLE LAYOUT FOR MEDIUM AND SMALL SCREENS -->
            <tbody v-else>
              <tr v-for="(offer, key) in availableOffersList" :key="key">
                <td v-if="!isNetworkSelected" label="Product">
                  <span class="font-weight-bold heading-medium">{{ offer.fuel_id | fueltype }}</span>
                </td>
                <td v-else label="Network">
                  <span class="font-weight-bold heading-medium">
                    {{ getNetworkName(offer.networkId, networks) }}
                  </span>
                </td>
                <td v-if="isContract" label="Contract Ref.">
                  <span> {{ offer.contract.reference }} </span>
                </td>
                <td label="Pricing Method">
                  <span>{{ offer.type_id | offerpricingmethod }}</span>
                </td>
                <td label="Bio Pricing">
                  <span>{{ offer.fuel_id | offerbiopricing }}</span>
                </td>
                <td label="Measurement">
                  <span>{{ offer.fuel_measurement_id | fuelmeasurementtype }}</span>
                </td>
                <td label="Lifting Period">
                  <span v-if="!isNetworkSelected"> {{ offer | liftingperiodformat }} </span>
                  <span v-else> &mdash; </span>
                </td>
                <td label="Product Settlement (USD/mt)">
                  <span v-if="offer.offerPricingBreakdown && offer.offerPricingBreakdown.baseProduct">{{
                    offer.offerPricingBreakdown.baseProduct.settlement | priceformat
                  }}</span>
                  <span v-else>&mdash;</span>
                </td>
                <td label="Bio-element Settlement (USD/mt)">
                  <span v-if="offer.offerPricingBreakdown && offer.offerPricingBreakdown.bioElement">{{
                    offer.offerPricingBreakdown.bioElement.settlement | priceformat
                  }}</span>
                  <span v-else>&mdash;</span>
                </td>
                <td label="FX Settlement GBP/USD">
                  <span>{{ fxSettlement | fxformat }}</span>
                </td>
                <td label="Density Conversion (L/mt)">
                  <span
                    v-if="
                      isPricingEngineEnabled && offer.offerPricingBreakdown && offer.offerPricingBreakdown.baseProduct
                    "
                    >{{ offer.offerPricingBreakdown.baseProduct.density | priceformat }}</span
                  >
                  <span v-else-if="offer.fuel && offer.fuel.lpt != null && offer.fuel.type === 'SPOT'">{{
                    offer.fuel.lpt | priceformat
                  }}</span>
                  <span v-else>&mdash;</span>
                </td>
                <td label="Bio-element Density Conversion (L/mt)">
                  <span
                    v-if="
                      isPricingEngineEnabled && offer.offerPricingBreakdown && offer.offerPricingBreakdown.bioElement
                    "
                    >{{ offer.offerPricingBreakdown.bioElement.density | priceformat }}</span
                  >
                  <span v-else-if="offer.fuel && offer.fuel.lpt != null && offer.fuel.type === 'NOT4SALE'">{{
                    offer.fuel.lpt | priceformat
                  }}</span>
                  <span v-else>&mdash;</span>
                </td>
                <td label="Duty (ppl)">
                  <span
                    v-if="
                      isPricingEngineEnabled && offer.offerPricingBreakdown && offer.offerPricingBreakdown.baseProduct
                    "
                    >{{ offer.offerPricingBreakdown.baseProduct.duty | priceformat }}</span
                  >
                  <span v-else-if="offer.fuel && (offer.fuel.duty != null) != null && offer.fuel.type === 'SPOT'"
                    >{{ offer.fuel.duty | priceformat }} hello</span
                  >
                  <span v-else>&mdash;</span>
                </td>
                <td label="Bio-element Duty (ppl)">
                  <span
                    v-if="
                      isPricingEngineEnabled && offer.offerPricingBreakdown && offer.offerPricingBreakdown.bioElement
                    "
                    >{{ offer.offerPricingBreakdown.bioElement.duty | priceformat }}</span
                  >
                  <span v-else-if="offer.fuel && offer.fuel.duty != null && offer.fuel.type === 'NOT4SALE'"
                    >{{ offer.fuel.duty | priceformat }} hello</span
                  >
                  <span v-else>&mdash;</span>
                </td>
                <td label="Settle Base Price (ppl)">
                  <span v-if="offer.offerPricingBreakdown">{{
                    offer.offerPricingBreakdown.settleBasePrice | priceformat
                  }}</span>
                  <span v-else>&mdash;</span>
                </td>
                <td label="Market Change (ppl)">
                  <span v-if="offer.type_id === 'TRACKING' && offer.offerPricingBreakdown">{{
                    offer.offerPricingBreakdown.productMarketChange | priceformat
                  }}</span>
                  <span v-else>&mdash;</span>
                </td>
                <td label="Live Base Price (ppl)">
                  <span v-if="offer.type_id === 'TRACKING' && offer.offerPricingBreakdown">{{
                    offer.offerPricingBreakdown.liveBasePrice | priceformat
                  }}</span>
                  <span v-else>&mdash;</span>
                </td>
                <td v-if="isPricingEngineEnabled" label="Cost Additions (ppl)">
                  <span v-if="offer.type_id !== 'FIXED' && offer.offerPricingBreakdown">{{
                    offer.offerPricingBreakdown.costAdditions | priceformat
                  }}</span>
                  <span v-else>&mdash;</span>
                </td>
                <td v-else label="Premium (ppl)">
                  <span v-if="offer.type_id !== 'FIXED'">{{ offer.premium | priceformat }}</span>
                  <span v-else>&mdash;</span>
                </td>
                <td label="Cust. Margin (ppl)">
                  <span v-if="offer.offerPricingBreakdown">{{
                    offer.offerPricingBreakdown.customerMargin | priceformat
                  }}</span>
                  <span v-else>&mdash;</span>
                </td>
                <td v-if="!isContract" label="Adjustment (ppl)">
                  <cleave
                    class="form-control w-50 ml-auto"
                    :options="numeral"
                    placeholder="Enter adjustment"
                    v-model="offer.adjustment"
                    @keyup.native="updateOfferTotalPrice(offer)"
                    :disabled="!offer.offerPricingBreakdown"
                  ></cleave>
                </td>
                <td v-if="offer.type_id !== 'SETTLEMENT' && isInvalidOfferQuotePrice(offer)" class="error-message">
                  Invalid adjustment price: quote price must be greater than 0
                </td>
                <td label="Quote Price (ppl)" :key="`quotePrice-${key}-${quotePriceRenderKey}`">
                  <span v-if="offer.type_id === 'SETTLEMENT'">Pending Settlement</span>
                  <cleave
                    v-else-if="!isContract"
                    class="form-control w-50 ml-auto"
                    :options="numeral"
                    :value="offer.quotePrice + haulageRate"
                    @keyup.native="onSetOfferQuotePrice($event.target.value, offer)"
                    :disabled="!offer.offerPricingBreakdown"
                  ></cleave>
                  <span v-else>{{ offer.quotePrice | priceformat }}</span>
                </td>
                <td v-if="offer.type_id !== 'SETTLEMENT' && isInvalidOfferQuotePrice(offer)" class="error-message">Quote price must be greater than 0</td>
                <td v-if="offer.contract" label="Current Month Volume/L">
                  <span> {{ offer.totalQuantityForMonth | volumeformat }} </span>
                </td>
                <td v-if="offer.contract" label="Volume Remaining/L">
                  <span> {{ offer.currentMaximumAvailable | volumeformat }} </span>
                </td>
                <td label="Volume/L">
                  <cleave
                    class="form-control w-50 ml-auto"
                    :options="numeralPositive"
                    :placeholder="maxMinPlaceholderValues(offer)"
                    v-model="offer.quantity"
                    @input="updateOfferTotalPrice(offer)"
                    :disabled="!offer.offerPricingBreakdown"
                  ></cleave>
                </td>
                <td v-if="checkOfferQuantityStatus(offer) === offerQuantityStatus.invalid" class="error-message">
                  {{ invalidQuantityErrorMessage(offer) }}
                </td>
                <td label="Total GBP">
                  <span>{{ (offer.totalPrice + (haulageRate / 100) * offer.quantity) | priceformat }}</span>
                </td>
                <td>
                  <button
                    class="btn btn-confirm"
                    :disabled="!areOfferParametersValid(offer)"
                    @click="$emit('add-offer-to-basket', offer, $event)"
                  >
                    Add to Basket
                  </button>
                </td>
              </tr>
              <!-- TABLE LAYOUT FOR MEDIUM AND SMALL SCREENS -->
            </tbody>
            <!-- OFFERS FOUND -->
          </table>

          <!-- SPINNER TO INDICATE THAT WE ARE LOADING THE DATA -->
          <div v-if="loadingOffers" class="spinner-container">
            <div class="spinner-border" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
          <span v-if="deliveryValidationError" class="error-message">Please check the selected delivery options</span>
          <!-- TABLE CONTAINER -->
        </div>
        <!-- MAIN COLUMN -->
      </div>
      <!-- ROW -->
    </div>
  </div>
</template>

<script>
import breakpoint from "@/mixins/index";
import { mapGetters } from "vuex";

import { volumeFormat } from "@/util/filters";
import { capitaliseFirstLetters, formatStringToNumber } from "@/util/util";
import {
  calculateOrderItemPrice,
  calculateFinalCustomerMargin,
  calculateFinalOfferPriceAndMargin,
} from "@/util/offers.js";
import { getNetworkName } from "@/util/networks";

export default {
  mixins: [breakpoint],
  data() {
    return {
      auditTrailShown: {
        costAdditions: false,
        customerMargin: false,
        offer: false,
      },
      offerBlockSelected: 0,
      offerBlocks: [],
      OFFER_BLOCK_MAX_LENGTH: 3,
      quotePriceRenderKey: 0,
      marginRenderKey: 0,
      offerQuantityStatus: {
        empty: 0,
        valid: 1,
        invalid: 2,
      },
    };
  },
  props: {
    offersList: {
      required: true,
      type: Array,
    },
    allFiltersSelected: {
      required: true,
      type: Boolean,
    },
    isServerError: {
      required: true,
      type: Boolean,
    },
    isBasketCreated: {
      required: true,
      type: Boolean,
    },
    haulageRate: {
      required: true,
      type: Number,
    },
    selectedCustomer: {
      required: false,
      type: Object,
      default: null,
    },
    isNetworkSelected: {
      required: false,
      type: Boolean,
      default: false,
    },
    loadingOffers: {
      required: false,
      type: Boolean,
      default: false,
    },
    deliveryValidationError: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters("inputCheckers", ["numeral", "numeralPositive"]),
    ...mapGetters("login", ["isPricingEngineEnabled"]),
    ...mapGetters("metadata", ["networks"]),
    ...mapGetters("livePrices", ["getFxSettlement", "getPrices"]),
    availableOffersList: function () {
      return this.offersList.filter(
        (offer) => offer.currentMaximumAvailable >= offer.minimumOrderQuantity && !offer.isAvailabilityDisabled
      );
    },
    fxSettlement: function () {
      return this.getFxSettlement;
    },
    costAdditionHeadings: function () {
      return this.getBreakDownHeadings("costAdditions", this.availableOffersList);
    },
    customerMarginHeadings: function () {
      return this.getBreakDownHeadings("customerMargin", this.availableOffersList);
    },
    offerBlockLengthDiffFromMaxLength: function () {
      const offerBlock = this.offerBlocks[this.offerBlockSelected];
      return this.OFFER_BLOCK_MAX_LENGTH - (offerBlock || []).length;
    },
    isContract: function () {
      return this.availableOffersList.length > 0 && this.availableOffersList[0].isContract;
    },
  },
  watch: {
    selectedCustomer: {
      handler: function () {},
      deep: true,
    },
    availableOffersList: {
      handler: function (offersList) {
        this.assignOffersList(offersList);
      },
    },
    haulageRate: {
      handler: function () {
        this.availableOffersList.forEach((offer) => {
          this.updateOfferTotalPrice(offer);
        });
      },
    },
  },
  methods: {
    assignOffersList: function (offers = []) {
      this.offerBlockSelected = 0;
      let offerBlockIdx = 0;
      let offerBlocksList = [];

      for (let [idx, offer] of offers.entries()) {
        // Distribute the list of offers into blocks of offers for display purposes
        if (
          offerBlocksList[offerBlockIdx] &&
          offerBlocksList[offerBlockIdx].length > 0 &&
          offerBlocksList[offerBlockIdx].length < this.OFFER_BLOCK_MAX_LENGTH
        ) {
          offerBlocksList[offerBlockIdx].push(offer);
        } else {
          if (idx > 0) {
            offerBlockIdx++;
          }
          offerBlocksList[offerBlockIdx] = [offer];
        }
      }
      // Now we can update the local list of offer blocks
      this.offerBlocks = offerBlocksList;
    },
    selectOfferBlock: function (direction) {
      if (direction === "NEXT") {
        // Go to next offer block
        if (this.offerBlockSelected < this.offerBlocks.length - 1) {
          this.offerBlockSelected++;
        }
      } else {
        // Go to previous offer block
        if (this.offerBlockSelected > 0) {
          this.offerBlockSelected--;
        }
      }
    },
    updateOfferTotalPrice: function (offer) {
      const { finalCustomerMargin, quotePrice, totalPrice } = calculateFinalOfferPriceAndMargin(offer);
      offer.finalCustomerMargin = finalCustomerMargin;
      offer.quotePrice = quotePrice;
      offer.totalPrice = totalPrice;

      this.quotePriceRenderKey++;
    },
    maxMinPlaceholderValues(offer) {
      if (offer.minimumOrderQuantity && offer.currentMaximumAvailable) {
        return `${volumeFormat(offer.minimumOrderQuantity)} - ${volumeFormat(offer.currentMaximumAvailable)} L`;
      }
      return "Enter Volume...";
    },
    areOfferParametersValid(offer) {
      if (offer.type_id === "SETTLEMENT") {
        return this.checkOfferQuantityStatus(offer) === this.offerQuantityStatus.valid;
      }
      return (
        offer.quotePrice &&
        !this.isInvalidOfferQuotePrice(offer) &&
        this.checkOfferQuantityStatus(offer) === this.offerQuantityStatus.valid
      );
    },
    checkOfferQuantityStatus(offer) {
      if (offer.quantity == null || offer.quantity === "") {
        return this.offerQuantityStatus.empty;
      } else if (offer.quantity < offer.minimumOrderQuantity || offer.quantity > offer.currentMaximumAvailable) {
        return this.offerQuantityStatus.invalid;
      }
      return this.offerQuantityStatus.valid;
    },
    isInvalidOfferQuotePrice(offer) {
      return offer.quotePrice <= 0;
    },
    invalidQuantityErrorMessage(offer) {
      return `Enter between ${volumeFormat(offer.minimumOrderQuantity)} - ${volumeFormat(
        offer.currentMaximumAvailable
      )}`;
    },
    onSetOfferQuotePrice: function (newDisplayPrice, offer) {
      // Calculate new adjustment based on inputted quote price, ensure it is rounded to 2 decimal places before object assignment
      offer.quotePrice = formatStringToNumber(newDisplayPrice) - this.haulageRate;
      const updatedAdjustment = offer.quotePrice - offer.price;
      offer.adjustment = Number(updatedAdjustment.toFixed(2)) === 0 ? null : updatedAdjustment.toFixed(2);

      offer.totalPrice = calculateOrderItemPrice(offer.quantity, offer.quotePrice);
      offer.finalCustomerMargin = calculateFinalCustomerMargin(offer);
      this.marginRenderKey++;
    },
    toggleAuditTrailBreakdown: function (auditTrailId) {
      this.auditTrailShown[auditTrailId] = !this.auditTrailShown[auditTrailId];
    },
    getBreakDownHeadings: function (breakDownType, offers) {
      const headings = {};
      offers.forEach((offer) => {
        const { offerPricingBreakdown } = offer;

        if (
          offerPricingBreakdown &&
          offerPricingBreakdown.auditTrail &&
          offerPricingBreakdown.auditTrail[breakDownType]
        ) {
          const rules = offerPricingBreakdown.auditTrail[breakDownType];
          rules.forEach((rule) => {
            if (!headings[rule._id]) {
              headings[rule._id] = `${capitaliseFirstLetters(rule.name)} (ppl)`; // By default all rules are set to ppl
            }
          });
        }
      });
      return headings;
    },
    ruleInOffer(offer, ruleType, ruleId) {
      if (
        offer.offerPricingBreakdown &&
        offer.offerPricingBreakdown.auditTrail &&
        offer.offerPricingBreakdown.auditTrail &&
        offer.offerPricingBreakdown.auditTrail[ruleType]
      ) {
        return offer.offerPricingBreakdown.auditTrail[ruleType].some((rule) => rule._id === ruleId);
      }
      return false;
    },
    getRuleFromOffer(offer, ruleType, ruleId) {
      return offer.offerPricingBreakdown.auditTrail[ruleType].find((rule) => rule._id === ruleId);
    },
    getNetworkName,
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/_spinner.scss";

tr.spacing-under > td {
  padding-bottom: 2em;
}
tr.spacing-under > th {
  padding-bottom: 2em;
}
tr > th,
td {
  vertical-align: top !important;
}
.negative-market-change {
  color: map-get($platform-theme, down-market-indicator-color);
}
</style>
